import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import toast from "../utils/toast";
import { getUserFriendlyErrorMessage } from "../utils/errors";
import { handleBackendResponse } from "../utils/backendResponseHandler";
import { rrFetch } from "../utils/rr_fetch"

export default class extends Controller {
  static values = {
    domain: String,
    company: { type: String, default: "CoinSub Wallet" },
    idempotencyKey: String,
    csrfToken: String,
  };

  connect() {
    this.isProcessing = false;
  }

  async removePasskey(event) {
    event.preventDefault();

    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    this.removeButtonDisabled();
    this.cancelButtonDisabled();
    this.showSpinner();

    try {
      const { challenge } = await this.requestDeletionChallenge();

      const signedChallenge = await this.signChallenge(challenge);

      await this.submitSignedChallenge({
        ...signedChallenge,
      });

      this.handleSuccess();
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message?.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      this.resetUIState();
    }
  }

  async requestDeletionChallenge() {
    const response = await rrFetch('/passkey/challenge/delete/request', {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    });

    return handleBackendResponse(response, "Failed to initiate passkey removal.");
  }

  async signChallenge(challengeData) {
    const currentDomain = window.location.hostname;
    const relyingPartyId = this.domainValue || currentDomain;
    const relyingPartyName = this.companyValue || "CoinSub Wallet";

    const webauthn = new WebAuthnSigner({
      relyingParty: {
        id: relyingPartyId,
        name: relyingPartyName,
      },
    });

    try {
      const assertion = await webauthn.sign(challengeData);
      const credentialAssertion = assertion.credentialAssertion || {};

      return {
        challengeIdentifier: challengeData.challengeIdentifier,
        signature: {
          kind: assertion.kind,
          credentialAssertion: {
            credId: credentialAssertion.credId || "",
            clientData: credentialAssertion.clientData || "",
            signature: credentialAssertion.signature || "",
            authenticatorData: credentialAssertion.authenticatorData || "",
            userHandle: credentialAssertion.userHandle || "",
          },
        },
      };
    } catch (error) {
      if (error.name === "InvalidStateError" || error.message?.includes("pending")) {
        toast("A passkey request is already in progress. Please refresh the page and try again.", "error");
        throw new Error("Passkey request already pending");
      }
      throw error;
    }
  }

  async submitSignedChallenge({ challengeIdentifier, signature }) {
    const response = await rrFetch("/passkey/challenge/delete/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: JSON.stringify({
        challengeIdentifier,
        signature,
        idempotencyKey: this.idempotencyKeyValue,
      }),
      credentials: "same-origin",
    });

    await handleBackendResponse(response, "Failed to remove passkey.");
  }

  handleSuccess() {
    this.isProcessing = false;
    setTimeout(() => {
      this.hideSpinner();
      Turbo.visit("/home", { action: "replace" });
    }, 1200);
  }

  resetUIState() {
    this.hideSpinner();
    this.enableRemoveButton();
    this.enableCancelButton();
    this.isProcessing = false;
  }

  removeButtonDisabled() {
    const button = document.getElementById("remove-button");
    const text = document.getElementById("remove-button-text");
    if (button) {
      button.disabled = true;
      button.classList.add("cursor-not-allowed", "opacity-50");
    }
    if (text) text.textContent = "Removing...";
  }

  enableRemoveButton() {
    const button = document.getElementById("remove-button");
    const text = document.getElementById("remove-button-text");
    if (button) {
      button.disabled = false;
      button.classList.remove("cursor-not-allowed", "opacity-50");
    }
    if (text) text.textContent = "Remove Passkey";
  }

  cancelButtonDisabled() {
    const button = document.getElementById("cancel-button");
    if (button) {
      button.style.pointerEvents = "none";
      button.classList.add("cursor-not-allowed", "opacity-50");
    }
  }

  enableCancelButton() {
    const button = document.getElementById("cancel-button");
    if (button) {
      button.style.pointerEvents = "";
      button.classList.remove("cursor-not-allowed", "opacity-50");
    }
  }

  showSpinner() {
    const spinner = document.getElementById("loading-spinner");
    if (spinner) spinner.classList.remove("hidden");
  }

  hideSpinner() {
    const spinner = document.getElementById("loading-spinner");
    if (spinner) spinner.classList.add("hidden");
  }
}
